/**
 * Study Action Dialog Store — Zustand store that bridges plain-function
 * action handlers with React dialog components.
 *
 * Action handlers in studyActions.ts set dialog state here.
 * Dialog components in the shell read from this store.
 */
import { create } from 'zustand';
import type { Study } from '../api/types';

export type DialogType =
  | 'assignStudy'
  | 'studyNotes'
  | 'studyLog'
  | 'editStudy'
  | 'sendStudy'
  | 'createReport'
  | 'editReport'
  | 'completeStudy'
  | 'editPatient'
  | 'peerReview'
  | 'studyLocation'
  | 'shareStudy'
  | 'missedSla'
  | 'arrivePatient'
  | 'confirmAction'
  | 'discordance'
  | 'callContacts'
  | 'attachments'
  | 'autoAddOrder'
  | 'editOrder'
  | 'addOrder'
  | 'scheduleOrder'
  | 'rescheduleOrder'
  | 'authOrder'
  | 'payment'
  | 'patientOrderHistory'
  | 'uploadRequest'
  | 'storageOptions'
  | 'patientDiscovery'
  | 'markImportant'
  | 'markFollowUp'
  | 'dicomDump'
  | null;

interface StudyActionState {
  /** Which dialog is currently open */
  activeDialog: DialogType;
  /** The study/studies the dialog is operating on */
  dialogStudies: Study[];
  /** Callback to run after dialog action completes */
  onComplete: (() => void) | null;
  /** Metadata for confirmAction dialog */
  confirmMeta: { title: string; message: string; action: () => Promise<unknown> } | null;

  /** Open a dialog for the given studies */
  openDialog: (type: DialogType, studies: Study[], onComplete?: () => void) => void;
  /** Open a confirm-action dialog with custom title/message/action */
  openConfirmAction: (
    studies: Study[],
    title: string,
    message: string,
    action: () => Promise<unknown>,
    onComplete?: () => void,
  ) => void;
  /** Close the active dialog */
  closeDialog: () => void;
}

export const useStudyActionStore = create<StudyActionState>((set) => ({
  activeDialog: null,
  dialogStudies: [],
  onComplete: null,
  confirmMeta: null,

  openDialog: (type, studies, onComplete) =>
    set({ activeDialog: type, dialogStudies: studies, onComplete: onComplete ?? null, confirmMeta: null }),

  openConfirmAction: (studies, title, message, action, onComplete) =>
    set({
      activeDialog: 'confirmAction',
      dialogStudies: studies,
      confirmMeta: { title, message, action },
      onComplete: onComplete ?? null,
    }),

  closeDialog: () =>
    set({ activeDialog: null, dialogStudies: [], onComplete: null, confirmMeta: null }),
}));
